<template>
  <div class="official-dashboard">
    <header class="dashboard-header">
      <div>
        <p class="dashboard-header__eyebrow">Burton · 正式小程序</p>
        <h1>销售日报数据看板</h1>
        <p class="dashboard-header__desc">按自然日统计访问、加购、支付、取消与退货指标。</p>
      </div>
      <div class="dashboard-actions">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          value-format="YYYY-MM-DD"
          :shortcuts="dateShortcuts"
          :clearable="false"
          unlink-panels
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          :disabled-date="disableFutureDate"
        />
        <el-button type="primary" :loading="loading" @click="loadDashboard">查询</el-button>
        <el-button type="success" plain :disabled="!dailyRows.length" @click="exportDailyReport">导出日报</el-button>
        <el-button type="warning" plain @click="openRealtime">实时销售额</el-button>
        <el-button @click="router.push('/burton-dashboard')">切换看板</el-button>
      </div>
    </header>

    <section class="meta-bar">
      <span>统计周期：{{ rangeText }}</span>
      <span>最近刷新：{{ lastRefreshTime || '-' }}</span>
      <span>看板刷新：{{ dashboardRefreshMinutes }} 分钟</span>
      <span>实时销售额刷新：{{ realtimeIntervalSeconds }} 秒</span>
    </section>

    <section class="kpi-grid" v-loading="loading">
      <article v-for="item in kpiCards" :key="item.key" class="kpi-card">
        <p class="kpi-card__label">{{ item.label }}</p>
        <p class="kpi-card__value">{{ item.value }}</p>
        <p class="kpi-card__kpi">{{ item.desc }}</p>
      </article>
    </section>

    <section class="chart-grid">
      <el-card class="panel-card" shadow="hover">
        <template #header>
          <div class="panel-header">
            <span>销售趋势对比</span>
            <span class="panel-tip">销售、取消、退货与有效销售</span>
          </div>
        </template>
        <div ref="salesTrendChartRef" class="chart-box"></div>
      </el-card>
      <el-card class="panel-card" shadow="hover">
        <template #header>
          <div class="panel-header">
            <span>访问与转化对比</span>
            <span class="panel-tip">访客、加购、有效销售人数</span>
          </div>
        </template>
        <div ref="conversionChartRef" class="chart-box"></div>
      </el-card>
      <el-card class="panel-card" shadow="hover">
        <template #header>
          <div class="panel-header">
            <span>订单构成</span>
            <span class="panel-tip">支付、取消、退货、有效订单</span>
          </div>
        </template>
        <div ref="orderMixChartRef" class="chart-box"></div>
      </el-card>
    </section>

    <el-card class="panel-card" shadow="hover" v-loading="loading">
      <template #header>
        <div class="panel-header">
          <span>按天明细</span>
          <span class="panel-tip">多天范围按天累加，金额单位为元</span>
        </div>
      </template>
      <el-table :data="dailyRows" stripe height="520">
        <el-table-column prop="date" label="日期" fixed width="120" />
        <el-table-column prop="paymentAmount" label="销售总额" min-width="120">
          <template #default="{ row }"><MetricCell :value="formatAmount(row.paymentAmount)" :mom="row.paymentAmountMom" /></template>
        </el-table-column>
        <el-table-column prop="cancelAmount" label="取消订单总额" min-width="130">
          <template #default="{ row }"><MetricCell :value="formatAmount(row.cancelAmount)" :mom="row.cancelAmountMom" /></template>
        </el-table-column>
        <el-table-column prop="returnAmount" label="退货总额" min-width="120">
          <template #default="{ row }"><MetricCell :value="formatAmount(row.returnAmount)" :mom="row.returnAmountMom" /></template>
        </el-table-column>
        <el-table-column prop="netSales" label="有效销售总额" min-width="130">
          <template #default="{ row }"><MetricCell :value="formatAmount(row.netSales)" :mom="row.netSalesMom" /></template>
        </el-table-column>
        <el-table-column prop="uv" label="访客人数" min-width="100">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.uv)" :mom="row.uvMom" /></template>
        </el-table-column>
        <el-table-column prop="pv" label="浏览页数" min-width="100">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.pv)" :mom="row.pvMom" /></template>
        </el-table-column>
        <el-table-column prop="pvPerUv" label="人均浏览页数" min-width="120">
          <template #default="{ row }">{{ formatDecimal(row.pvPerUv) }}</template>
        </el-table-column>
        <el-table-column prop="cartUsers" label="加购人数" min-width="100">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.cartUsers)" :mom="row.cartUsersMom" /></template>
        </el-table-column>
        <el-table-column prop="cartRate" label="加购率" min-width="90">
          <template #default="{ row }">{{ formatPercent(row.cartRate) }}</template>
        </el-table-column>
        <el-table-column prop="paymentOrders" label="订单数" min-width="90">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.paymentOrders)" :mom="row.paymentOrdersMom" /></template>
        </el-table-column>
        <el-table-column prop="cancelOrders" label="取消订单数" min-width="120">
          <template #default="{ row }">{{ formatNumber(row.cancelOrders) }}</template>
        </el-table-column>
        <el-table-column prop="returnOrders" label="退货订单数" min-width="120">
          <template #default="{ row }">{{ formatNumber(row.returnOrders) }}</template>
        </el-table-column>
        <el-table-column prop="netOrders" label="有效订单总数" min-width="130">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.netOrders)" :mom="row.netOrdersMom" /></template>
        </el-table-column>
        <el-table-column prop="paymentUnits" label="销货件数" min-width="100">
          <template #default="{ row }">{{ formatNumber(row.paymentUnits) }}</template>
        </el-table-column>
        <el-table-column prop="cancelUnits" label="取消订单内含件数" min-width="150">
          <template #default="{ row }">{{ formatNumber(row.cancelUnits) }}</template>
        </el-table-column>
        <el-table-column prop="returnUnits" label="退货订单内含件数" min-width="150">
          <template #default="{ row }">{{ formatNumber(row.returnUnits) }}</template>
        </el-table-column>
        <el-table-column prop="netQty" label="有效销货总件数" min-width="140">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.netQty)" :mom="row.netQtyMom" /></template>
        </el-table-column>
        <el-table-column prop="customers" label="有效销售人数" min-width="120">
          <template #default="{ row }"><MetricCell :value="formatNumber(row.customers)" :mom="row.customersMom" /></template>
        </el-table-column>
        <el-table-column prop="adpt" label="客单价" min-width="90">
          <template #default="{ row }">{{ formatAmount(row.adpt) }}</template>
        </el-table-column>
        <el-table-column prop="upt" label="客单件" min-width="90">
          <template #default="{ row }">{{ formatDecimal(row.upt) }}</template>
        </el-table-column>
        <el-table-column prop="apu" label="件单价" min-width="90">
          <template #default="{ row }">{{ formatAmount(row.apu) }}</template>
        </el-table-column>
        <el-table-column prop="cancelRate" label="取消订单比率" min-width="120">
          <template #default="{ row }">{{ formatPercent(row.cancelRate) }}</template>
        </el-table-column>
        <el-table-column prop="returnRate" label="退货订单比率" min-width="120">
          <template #default="{ row }">{{ formatPercent(row.returnRate) }}</template>
        </el-table-column>
        <el-table-column prop="conversionRate" label="访客转化比率" min-width="120">
          <template #default="{ row }">{{ formatPercent(row.conversionRate) }}</template>
        </el-table-column>
        <el-table-column prop="uvValue" label="访客价值" min-width="100">
          <template #default="{ row }">{{ formatAmount(row.uvValue) }}</template>
        </el-table-column>
      </el-table>
    </el-card>

    <div v-if="realtimeVisible" class="realtime-overlay">
      <button
        type="button"
        class="realtime-pull-switch"
        :class="{ 'is-active': realtimeControlsVisible }"
        @click="toggleRealtimeControls"
      >
        <span></span>
      </button>
      <div
        class="realtime-toolbar"
        :class="{ 'is-hidden': !realtimeControlsVisible }"
        @mouseenter="showRealtimeControls"
      >
        <div>
          <p class="dashboard-header__eyebrow">实时销售</p>
          <h2>实时销售额</h2>
        </div>
        <div class="realtime-actions">
          <el-radio-group v-model="realtimePeriod" size="large">
            <el-radio-button label="today">今日</el-radio-button>
            <el-radio-button label="online">上线以来</el-radio-button>
          </el-radio-group>
          <el-radio-group v-model="realtimeMode" size="large">
            <el-radio-button label="order">下单金额</el-radio-button>
            <el-radio-button label="pay">支付金额</el-radio-button>
            <el-radio-button label="both">全部</el-radio-button>
          </el-radio-group>
          <el-input-number v-model="realtimeIntervalSeconds" :min="1" :max="60" size="large" />
          <el-button size="large" @click="closeRealtime">关闭</el-button>
        </div>
      </div>
      <div class="realtime-content">
        <article v-if="realtimeMode !== 'pay'" class="realtime-metric">
          <span>下单金额</span>
          <strong>{{ formatAmount(animatedRealtime.orderAmount) }}</strong>
        </article>
        <article v-if="realtimeMode !== 'order'" class="realtime-metric realtime-metric--pay">
          <span>支付金额</span>
          <strong>{{ formatAmount(animatedRealtime.payAmount) }}</strong>
        </article>
      </div>
      <div class="realtime-foot">
        <span>{{ realtimePeriod === 'online' ? '上线以来至当前' : '今日 00:00 至当前' }}</span>
        <span>最近返回：{{ realtimeLastFetchTime || '-' }}</span>
        <span v-if="realtimeLoading">查询中...</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import * as XLSX from 'xlsx'
import * as echarts from 'echarts'
import {
  BURTON_CONFIG_OFFICIAL,
  execBurtonOrderSql,
  fetchBurtonDataBoard,
  getBurtonStoreConfig,
  loginBurtonStore
} from '@/utils/burtonDashboardApi'

const CANCEL_ORDER_STATUSES = ['TRADE_CLOSED_BY_BUYER', 'PAYED_TRADE_CANCELED_BY_BUYER']
const CANCEL_SERVICE_TYPES = ['CANCEL_ORDER']
const RETURN_SERVICE_TYPES = ['REFUND_ONLY', 'REFUND_OTHER', 'RETURN_AND_REFUND']
const SUCCESS_SERVICE_STATUSES = ['SUCCESS']

const router = useRouter()
const loading = ref(false)
const dateRange = ref([])
const dailyRows = ref([])
const lastRefreshTime = ref('')
const dashboardConfig = ref(null)
const dashboardToken = ref('')
const realtimeVisible = ref(false)
const realtimeMode = ref('both')
const realtimePeriod = ref('today')
const realtimeControlsVisible = ref(true)
const realtimeIntervalSeconds = ref(2)
const dashboardRefreshMinutes = ref(30)
const realtimeLoading = ref(false)
const realtimeLastFetchTime = ref('')
const realtimeTarget = reactive({ orderAmount: 0, payAmount: 0 })
const animatedRealtime = reactive({ orderAmount: 0, payAmount: 0 })
const salesTrendChartRef = ref(null)
const conversionChartRef = ref(null)
const orderMixChartRef = ref(null)

let realtimeTimer = null
let realtimeAnimationFrame = null
let realtimeControlsTimer = null
let dashboardRefreshTimer = null
let salesTrendChart = null
let conversionChart = null
let orderMixChart = null
let suppressDateAutoQuery = true
let dateAutoQueryTimer = null

const MetricCell = {
  props: {
    value: { type: String, required: true },
    mom: { type: Number, default: null }
  },
  computed: {
    momText() {
      if (this.mom === null || this.mom === undefined || Number.isNaN(this.mom)) return ''
      if (!Number.isFinite(this.mom)) return '+∞'
      const sign = this.mom > 0 ? '+' : ''
      return `${sign}${(this.mom * 100).toFixed(2)}%`
    },
    momClass() {
      if (this.mom === null || this.mom === undefined || Number.isNaN(this.mom) || this.mom === 0) return 'is-flat'
      return this.mom > 0 ? 'is-up' : 'is-down'
    }
  },
  template: `
    <div class="metric-cell">
      <span>{{ value }}</span>
      <small v-if="momText" :class="momClass">环比 {{ momText }}</small>
    </div>
  `
}

const summary = computed(() => {
  return withFormulaFields(
    dailyRows.value.reduce((acc, row) => {
      RAW_FIELDS.forEach((key) => {
        acc[key] += toNumber(row[key])
      })
      return acc
    }, createEmptyRawRow('合计'))
  )
})

const kpiCards = computed(() => [
  { key: 'paymentAmount', label: '销售总额', desc: '已支付订单实付金额', value: formatAmount(summary.value.paymentAmount) },
  { key: 'netSales', label: '有效销售总额', desc: '销售总额减取消与退货', value: formatAmount(summary.value.netSales) },
  { key: 'uv', label: '访客人数', desc: '访问小程序的去重用户数', value: formatNumber(summary.value.uv) },
  { key: 'pv', label: '浏览页数', desc: '小程序页面浏览次数', value: formatNumber(summary.value.pv) },
  { key: 'cartRate', label: '加购率', desc: '加购人数除以访客人数', value: formatPercent(summary.value.cartRate) },
  { key: 'paymentOrders', label: '订单数', desc: '完成支付的订单总数', value: formatNumber(summary.value.paymentOrders) },
  { key: 'netOrders', label: '有效订单总数', desc: '订单数减取消与退货订单', value: formatNumber(summary.value.netOrders) },
  { key: 'netQty', label: '有效销货总件数', desc: '销货件数减取消与退货件数', value: formatNumber(summary.value.netQty) },
  { key: 'customers', label: '有效销售人数', desc: '完成有效购买的去重用户数', value: formatNumber(summary.value.customers) },
  { key: 'conversionRate', label: '访客转化比率', desc: '有效销售人数除以访客人数', value: formatPercent(summary.value.conversionRate) },
  { key: 'uvValue', label: '访客价值', desc: '销售总额除以访客人数', value: formatAmount(summary.value.uvValue) },
  { key: 'adpt', label: '客单价', desc: '有效销售总额除以有效销售人数', value: formatAmount(summary.value.adpt) }
])

const rangeText = computed(() => {
  if (!dateRange.value?.length) return '-'
  return `${dateRange.value[0]} ~ ${dateRange.value[1]}`
})

const dateShortcuts = computed(() => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const yesterday = addDays(today, -1)
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  const launchStart = parseConfigStartDate() || today
  return [
    { text: '上线以来', value: () => [launchStart, today] },
    { text: '今天', value: () => [today, today] },
    { text: '昨天', value: () => [yesterday, yesterday] },
    { text: '近7天', value: () => [addDays(today, -6), today] },
    { text: '近30天', value: () => [addDays(today, -29), today] },
    { text: '本月', value: () => [monthStart, today] }
  ]
})

const RAW_FIELDS = [
  'paymentAmount',
  'cancelAmount',
  'returnAmount',
  'uv',
  'pv',
  'cartUsers',
  'paymentOrders',
  'cancelOrders',
  'returnOrders',
  'paymentUnits',
  'cancelUnits',
  'returnUnits',
  'customers'
]

const EXCEL_COLUMNS = [
  ['日期', 'date'],
  ['销售总额', 'paymentAmount', 'amount'],
  ['取消订单总额', 'cancelAmount', 'amount'],
  ['退货总额', 'returnAmount', 'amount'],
  ['有效销售总额', 'netSales', 'amount'],
  ['访客人数', 'uv', 'number'],
  ['浏览页数', 'pv', 'number'],
  ['人均浏览页数', 'pvPerUv', 'decimal'],
  ['加购人数', 'cartUsers', 'number'],
  ['加购率', 'cartRate', 'percent'],
  ['订单数', 'paymentOrders', 'number'],
  ['取消订单数', 'cancelOrders', 'number'],
  ['退货订单数', 'returnOrders', 'number'],
  ['有效订单总数', 'netOrders', 'number'],
  ['销货件数', 'paymentUnits', 'number'],
  ['取消订单内含件数', 'cancelUnits', 'number'],
  ['退货订单内含件数', 'returnUnits', 'number'],
  ['有效销货总件数', 'netQty', 'number'],
  ['有效销售人数', 'customers', 'number'],
  ['客单价', 'adpt', 'amount'],
  ['客单件', 'upt', 'decimal'],
  ['件单价', 'apu', 'amount'],
  ['取消订单比率', 'cancelRate', 'percent'],
  ['退货订单比率', 'returnRate', 'percent'],
  ['访客转化比率', 'conversionRate', 'percent'],
  ['访客价值', 'uvValue', 'amount']
]

function createEmptyRawRow(date) {
  return RAW_FIELDS.reduce((row, key) => {
    row[key] = 0
    return row
  }, { date })
}

function withFormulaFields(row) {
  const result = { ...row }
  result.netSales = result.paymentAmount - result.cancelAmount - result.returnAmount
  result.pvPerUv = calcRate(result.pv, result.uv)
  result.cartRate = calcRate(result.cartUsers, result.uv)
  result.netOrders = result.paymentOrders - result.cancelOrders - result.returnOrders
  result.netQty = result.paymentUnits - result.cancelUnits - result.returnUnits
  result.adpt = calcRate(result.netSales, result.customers)
  result.upt = calcRate(result.netQty, result.netOrders)
  result.apu = calcRate(result.netSales, result.netQty)
  result.cancelRate = calcRate(result.cancelOrders, result.paymentOrders)
  result.returnRate = calcRate(result.returnOrders, result.netOrders)
  result.conversionRate = calcRate(result.customers, result.uv)
  result.uvValue = calcRate(result.paymentAmount, result.uv)
  return result
}

function toNumber(value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

function calcRate(numerator, denominator) {
  const d = toNumber(denominator)
  if (!d) return 0
  return toNumber(numerator) / d
}

function pad2(value) {
  return String(value).padStart(2, '0')
}

function formatDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`
}

function formatDateTime(date) {
  return `${formatDate(date)} ${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`
}

function parseDateStart(text) {
  return new Date(`${String(text).slice(0, 10)}T00:00:00`)
}

function parseConfigStartDate() {
  const value = dashboardConfig.value?.project_start_time
  if (!value) return null
  const parsed = parseDateStart(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function addDays(date, days) {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

function disableFutureDate(date) {
  const tomorrow = new Date()
  tomorrow.setHours(24, 0, 0, 0)
  return date.getTime() >= tomorrow.getTime()
}

function formatNumber(value) {
  return toNumber(value).toLocaleString('zh-CN')
}

function formatDecimal(value) {
  return toNumber(value).toLocaleString('zh-CN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
}

function formatAmount(value) {
  return `￥${formatDecimal(value)}`
}

function formatPercent(value) {
  return `${(toNumber(value) * 100).toFixed(2)}%`
}

function calcMom(current, previous) {
  const curr = toNumber(current)
  const prev = toNumber(previous)
  if (!prev) return curr ? Infinity : null
  return (curr - prev) / prev
}

function enrichMomRows(rows) {
  return rows.map((row, index) => {
    if (index === 0) return row
    const prev = rows[index - 1]
    const momFields = [
      'paymentAmount',
      'cancelAmount',
      'returnAmount',
      'netSales',
      'uv',
      'pv',
      'cartUsers',
      'paymentOrders',
      'netOrders',
      'netQty',
      'customers'
    ]
    return momFields.reduce((next, field) => {
      next[`${field}Mom`] = calcMom(row[field], prev[field])
      return next
    }, { ...row })
  })
}

function initCharts() {
  if (salesTrendChartRef.value && !salesTrendChart) {
    salesTrendChart = echarts.init(salesTrendChartRef.value)
  }
  if (conversionChartRef.value && !conversionChart) {
    conversionChart = echarts.init(conversionChartRef.value)
  }
  if (orderMixChartRef.value && !orderMixChart) {
    orderMixChart = echarts.init(orderMixChartRef.value)
  }
}

function renderCharts() {
  initCharts()
  const rows = dailyRows.value || []
  const dates = rows.map((row) => row.date)
  if (salesTrendChart) {
    salesTrendChart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { top: 44, left: 56, right: 18, bottom: 36 },
      xAxis: { type: 'category', data: dates },
      yAxis: { type: 'value', axisLabel: { formatter: (value) => `${Number(value / 10000).toFixed(1)}万` } },
      series: [
        { name: '销售总额', type: 'line', smooth: true, data: rows.map((row) => row.paymentAmount) },
        { name: '有效销售总额', type: 'line', smooth: true, data: rows.map((row) => row.netSales) },
        { name: '取消订单总额', type: 'bar', data: rows.map((row) => row.cancelAmount) },
        { name: '退货总额', type: 'bar', data: rows.map((row) => row.returnAmount) }
      ]
    }, true)
  }
  if (conversionChart) {
    conversionChart.setOption({
      tooltip: { trigger: 'axis' },
      legend: { top: 0 },
      grid: { top: 44, left: 46, right: 18, bottom: 36 },
      xAxis: { type: 'category', data: dates },
      yAxis: { type: 'value' },
      series: [
        { name: '访客人数', type: 'bar', data: rows.map((row) => row.uv) },
        { name: '加购人数', type: 'bar', data: rows.map((row) => row.cartUsers) },
        { name: '有效销售人数', type: 'line', smooth: true, data: rows.map((row) => row.customers) }
      ]
    }, true)
  }
  if (orderMixChart) {
    const data = [
      { name: '订单数', value: summary.value.paymentOrders },
      { name: '取消订单数', value: summary.value.cancelOrders },
      { name: '退货订单数', value: summary.value.returnOrders },
      { name: '有效订单总数', value: summary.value.netOrders }
    ].filter((item) => item.value > 0)
    orderMixChart.setOption({
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      series: [
        {
          type: 'pie',
          radius: ['38%', '68%'],
          center: ['50%', '45%'],
          label: { formatter: '{b}\\n{c}' },
          data
        }
      ]
    }, true)
  }
}

function resizeCharts() {
  if (salesTrendChart) salesTrendChart.resize()
  if (conversionChart) conversionChart.resize()
  if (orderMixChart) orderMixChart.resize()
}

function sqlIn(values) {
  return values.map((item) => `'${item}'`).join(',')
}

function getAaData(result) {
  if (Array.isArray(result?.aaData)) return result.aaData
  if (Array.isArray(result?.data)) return result.data
  if (Array.isArray(result)) return result
  return []
}

function toSnakeKey(key) {
  return String(key).replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

function getRowValue(row, key) {
  return row[key] ?? row[toSnakeKey(key)] ?? row[String(key).toLowerCase()]
}

function mapRowsByDate(rows, dateField, valueFields) {
  const result = {}
  rows.forEach((row) => {
    const date = String(getRowValue(row, dateField) || '').slice(0, 10)
    if (!date) return
    if (!result[date]) result[date] = {}
    valueFields.forEach((field) => {
      result[date][field] = toNumber(getRowValue(row, field))
    })
  })
  return result
}

function buildSalesDailySql(start, end) {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  return `
SELECT pay_day AS day,
       SUM(payment_amount) AS paymentAmount,
       SUM(payment_orders) AS paymentOrders,
       SUM(payment_units) AS paymentUnits
FROM (
  SELECT DATE_FORMAT(o.pay_time, '%Y-%m-%d') AS pay_day,
         SUM(COALESCE(o.payment, 0)) AS payment_amount,
         COUNT(DISTINCT o.id) AS payment_orders,
         SUM(COALESCE(oi.total_quantity, 0)) AS payment_units
  FROM store_order o
  LEFT JOIN (
    SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
    FROM order_item
    WHERE isdeleted = 0
    GROUP BY order_id
  ) oi ON oi.order_id = o.id
  WHERE o.isdeleted = 0
    AND o.pay_time IS NOT NULL
    AND o.pay_time >= '${startText}'
    AND o.pay_time < '${endText}'
  GROUP BY DATE_FORMAT(o.pay_time, '%Y-%m-%d')
) pay
GROUP BY pay_day
ORDER BY pay_day ASC
;`.trim()
}

function buildCancelDailySql(start, end) {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  return `
SELECT DATE_FORMAT(so.end_time, '%Y-%m-%d') AS day,
       SUM(COALESCE(so.actual_refund_amount, so.refund_amount, o.payment, 0)) AS cancelAmount,
       COUNT(DISTINCT so.order_id) AS cancelOrders,
       SUM(COALESCE(soi.cancel_quantity, oi.total_quantity, 0)) AS cancelUnits
FROM service_order so
LEFT JOIN store_order o ON o.id = so.order_id
LEFT JOIN (
  SELECT service_order_id, SUM(COALESCE(quantity, 0)) AS cancel_quantity
  FROM service_order_item
  WHERE isdeleted = 0
  GROUP BY service_order_id
) soi ON soi.service_order_id = so.id
LEFT JOIN (
  SELECT order_id, SUM(COALESCE(quantity, 0)) AS total_quantity
  FROM order_item
  WHERE isdeleted = 0
  GROUP BY order_id
) oi ON oi.order_id = so.order_id
WHERE so.isdeleted = 0
  AND so.service_type IN (${sqlIn(CANCEL_SERVICE_TYPES)})
  AND so.status IN (${sqlIn(SUCCESS_SERVICE_STATUSES)})
  AND so.end_time IS NOT NULL
  AND so.end_time >= '${startText}'
  AND so.end_time < '${endText}'
GROUP BY DATE_FORMAT(so.end_time, '%Y-%m-%d')
ORDER BY day ASC
;`.trim()
}

function buildReturnDailySql(start, end) {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  return `
SELECT DATE_FORMAT(so.end_time, '%Y-%m-%d') AS day,
       SUM(COALESCE(so.actual_refund_amount, so.refund_amount, 0)) AS returnAmount,
       COUNT(DISTINCT so.order_id) AS returnOrders,
       SUM(COALESCE(soi.return_quantity, 0)) AS returnUnits
FROM service_order so
LEFT JOIN (
  SELECT service_order_id, SUM(COALESCE(quantity, 0)) AS return_quantity
  FROM service_order_item
  WHERE isdeleted = 0
  GROUP BY service_order_id
) soi ON soi.service_order_id = so.id
WHERE so.isdeleted = 0
  AND so.service_type IN (${sqlIn(RETURN_SERVICE_TYPES)})
  AND so.status IN (${sqlIn(SUCCESS_SERVICE_STATUSES)})
  AND so.end_time IS NOT NULL
  AND so.end_time >= '${startText}'
  AND so.end_time < '${endText}'
  AND COALESCE(so.actual_refund_amount, so.refund_amount, 0) > 0
GROUP BY DATE_FORMAT(so.end_time, '%Y-%m-%d')
ORDER BY day ASC
;`.trim()
}

function buildCartDailySql(start, end) {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  return `
SELECT DATE_FORMAT(add_time, '%Y-%m-%d') AS day,
       COUNT(DISTINCT user_id) AS cartUsers
FROM cart_record
WHERE isdeleted = 0
  AND add_time >= '${startText}'
  AND add_time < '${endText}'
GROUP BY DATE_FORMAT(add_time, '%Y-%m-%d')
ORDER BY day ASC
;`.trim()
}

function buildCustomersDailySql(start, end) {
  const startText = formatDateTime(start)
  const endText = formatDateTime(end)
  return `
SELECT DATE_FORMAT(o.pay_time, '%Y-%m-%d') AS day,
       COUNT(DISTINCT o.buyer_id) AS customers
FROM store_order o
WHERE o.isdeleted = 0
  AND o.pay_time IS NOT NULL
  AND o.pay_time >= '${startText}'
  AND o.pay_time < '${endText}'
  AND o.order_status NOT IN (${sqlIn(CANCEL_ORDER_STATUSES)})
  AND NOT EXISTS (
    SELECT 1
    FROM service_order so
    WHERE so.isdeleted = 0
      AND so.order_id = o.id
      AND COALESCE(so.actual_refund_amount, so.refund_amount, 0) > 0
      AND so.status IN (${sqlIn(SUCCESS_SERVICE_STATUSES)})
      AND so.service_type IN (${sqlIn([...CANCEL_SERVICE_TYPES, ...RETURN_SERVICE_TYPES])})
  )
GROUP BY DATE_FORMAT(o.pay_time, '%Y-%m-%d')
ORDER BY day ASC
;`.trim()
}

function getRealtimeStartDate() {
  const now = new Date()
  if (realtimePeriod.value === 'online') {
    const launchStart = parseConfigStartDate()
    if (launchStart) return launchStart
  }
  const start = new Date(now)
  start.setHours(0, 0, 0, 0)
  return start
}

function buildRealtimeSalesSql() {
  const now = new Date()
  const start = getRealtimeStartDate()
  return `
SELECT
  (
    SELECT SUM(COALESCE(actual_pay_amount, 0) + COALESCE(shipping_fee, 0))
    FROM store_order
    WHERE isdeleted = 0
      AND create_time >= '${formatDateTime(start)}'
      AND create_time <= '${formatDateTime(now)}'
  ) AS orderAmount,
  (
    SELECT SUM(COALESCE(payment, 0))
    FROM store_order
    WHERE isdeleted = 0
      AND pay_time IS NOT NULL
      AND pay_time >= '${formatDateTime(start)}'
      AND pay_time <= '${formatDateTime(now)}'
  ) AS payAmount
;`.trim()
}

async function ensureOfficialSession() {
  if (!dashboardConfig.value) {
    dashboardConfig.value = await getBurtonStoreConfig(BURTON_CONFIG_OFFICIAL)
    realtimeIntervalSeconds.value = Number(dashboardConfig.value.realtime_refresh_seconds || 2)
    dashboardRefreshMinutes.value = Number(dashboardConfig.value.dashboard_refresh_minutes || 30)
  }
  if (!dashboardToken.value) {
    dashboardToken.value = await loginBurtonStore(dashboardConfig.value)
  }
  return {
    config: dashboardConfig.value,
    token: dashboardToken.value
  }
}

async function loadDashboard() {
  if (!dateRange.value?.[0] || !dateRange.value?.[1]) return
  loading.value = true
  try {
    const start = parseDateStart(dateRange.value[0])
    const end = addDays(parseDateStart(dateRange.value[1]), 1)
    const dayKeys = []
    for (let cursor = new Date(start); cursor < end; cursor = addDays(cursor, 1)) {
      dayKeys.push(formatDate(cursor))
    }

    const { config, token } = await ensureOfficialSession()
    const [
      boardRows,
      salesResult,
      cancelResult,
      returnResult,
      cartResult,
      customersResult
    ] = await Promise.all([
      fetchBurtonDataBoard(config, token, {
        startDate: formatDateTime(start),
        endDate: formatDateTime(end),
        isHour: false
      }),
      execBurtonOrderSql(config, token, buildSalesDailySql(start, end)),
      execBurtonOrderSql(config, token, buildCancelDailySql(start, end)),
      execBurtonOrderSql(config, token, buildReturnDailySql(start, end)),
      execBurtonOrderSql(config, token, buildCartDailySql(start, end)),
      execBurtonOrderSql(config, token, buildCustomersDailySql(start, end))
    ])

    const boardMap = {}
    ;(Array.isArray(boardRows) ? boardRows : []).forEach((row) => {
      const date = String(row.dataTime || row.data_time || row.day || '').slice(0, 10)
      if (!date) return
      boardMap[date] = {
        uv: toNumber(row.uv),
        pv: toNumber(row.pv)
      }
    })
    const salesMap = mapRowsByDate(getAaData(salesResult), 'day', ['paymentAmount', 'paymentOrders', 'paymentUnits'])
    const cancelMap = mapRowsByDate(getAaData(cancelResult), 'day', ['cancelAmount', 'cancelOrders', 'cancelUnits'])
    const returnMap = mapRowsByDate(getAaData(returnResult), 'day', ['returnAmount', 'returnOrders', 'returnUnits'])
    const cartMap = mapRowsByDate(getAaData(cartResult), 'day', ['cartUsers'])
    const customersMap = mapRowsByDate(getAaData(customersResult), 'day', ['customers'])

    dailyRows.value = enrichMomRows(dayKeys.map((date) => withFormulaFields({
      ...createEmptyRawRow(date),
      ...(boardMap[date] || {}),
      ...(salesMap[date] || {}),
      ...(cancelMap[date] || {}),
      ...(returnMap[date] || {}),
      ...(cartMap[date] || {}),
      ...(customersMap[date] || {})
    })))
    await nextTick()
    renderCharts()
    lastRefreshTime.value = formatDateTime(new Date())
  } catch (error) {
    console.error('加载正式小程序看板失败:', error)
    ElMessage.error(error?.message || '加载正式小程序看板失败')
  } finally {
    loading.value = false
  }
}

function exportDailyReport() {
  const rows = [...dailyRows.value, summary.value]
  const data = [
    EXCEL_COLUMNS.map(([label]) => label),
    ...rows.map((row) => EXCEL_COLUMNS.map(([, key]) => row[key]))
  ]
  const sheet = XLSX.utils.aoa_to_sheet(data)
  EXCEL_COLUMNS.forEach(([, , type], index) => {
    const col = XLSX.utils.encode_col(index)
    for (let rowIndex = 2; rowIndex <= rows.length + 1; rowIndex += 1) {
      const cell = sheet[`${col}${rowIndex}`]
      if (!cell || type === 'date') continue
      if (type === 'amount' || type === 'decimal') cell.z = '#,##0.00'
      if (type === 'percent') cell.z = '0.00%'
    }
  })
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, sheet, 'Sales Daily Report')
  XLSX.writeFile(workbook, `Burton正式小程序销售日报_${Date.now()}.xlsx`)
}

function animateRealtimeTo(nextTarget) {
  const from = {
    orderAmount: animatedRealtime.orderAmount,
    payAmount: animatedRealtime.payAmount
  }
  realtimeTarget.orderAmount = toNumber(nextTarget.orderAmount)
  realtimeTarget.payAmount = toNumber(nextTarget.payAmount)
  const startTs = performance.now()
  const duration = Math.max(800, realtimeIntervalSeconds.value * 1000 * 0.85)

  if (realtimeAnimationFrame) cancelAnimationFrame(realtimeAnimationFrame)

  const tick = (now) => {
    const progress = Math.min(1, (now - startTs) / duration)
    const eased = 1 - Math.pow(1 - progress, 3)
    animatedRealtime.orderAmount = from.orderAmount + (realtimeTarget.orderAmount - from.orderAmount) * eased
    animatedRealtime.payAmount = from.payAmount + (realtimeTarget.payAmount - from.payAmount) * eased
    if (progress < 1 && realtimeVisible.value) {
      realtimeAnimationFrame = requestAnimationFrame(tick)
    }
  }
  realtimeAnimationFrame = requestAnimationFrame(tick)
}

async function fetchRealtimeSales() {
  if (!realtimeVisible.value || realtimeLoading.value) return
  realtimeLoading.value = true
  try {
    const { config, token } = await ensureOfficialSession()
    const result = await execBurtonOrderSql(config, token, buildRealtimeSalesSql())
    const row = getAaData(result)[0] || {}
    animateRealtimeTo({
      orderAmount: row.orderAmount ?? row.order_amount,
      payAmount: row.payAmount ?? row.pay_amount
    })
    realtimeLastFetchTime.value = formatDateTime(new Date())
  } catch (error) {
    console.error('查询实时销售额失败:', error)
    ElMessage.error(error?.message || '查询实时销售额失败')
  } finally {
    realtimeLoading.value = false
  }
}

function stopRealtimeTimer() {
  if (realtimeTimer) {
    clearInterval(realtimeTimer)
    realtimeTimer = null
  }
  if (realtimeAnimationFrame) {
    cancelAnimationFrame(realtimeAnimationFrame)
    realtimeAnimationFrame = null
  }
  clearRealtimeControlsTimer()
}

function stopDashboardRefreshTimer() {
  if (dashboardRefreshTimer) {
    clearInterval(dashboardRefreshTimer)
    dashboardRefreshTimer = null
  }
}

function startDashboardRefreshTimer() {
  stopDashboardRefreshTimer()
  const minutes = Math.max(5, toNumber(dashboardRefreshMinutes.value) || 30)
  dashboardRefreshTimer = setInterval(() => {
    loadDashboard()
  }, minutes * 60 * 1000)
}

function startRealtimeTimer() {
  stopRealtimeTimer()
  fetchRealtimeSales()
  realtimeTimer = setInterval(fetchRealtimeSales, Math.max(1, toNumber(realtimeIntervalSeconds.value)) * 1000)
}

function openRealtime() {
  realtimeVisible.value = true
  showRealtimeControls()
  startRealtimeTimer()
}

function closeRealtime() {
  realtimeVisible.value = false
  stopRealtimeTimer()
}

function clearRealtimeControlsTimer() {
  if (realtimeControlsTimer) {
    clearTimeout(realtimeControlsTimer)
    realtimeControlsTimer = null
  }
}

function showRealtimeControls() {
  realtimeControlsVisible.value = true
  clearRealtimeControlsTimer()
  realtimeControlsTimer = setTimeout(() => {
    realtimeControlsVisible.value = false
  }, 4200)
}

function toggleRealtimeControls() {
  if (realtimeControlsVisible.value) {
    realtimeControlsVisible.value = false
    clearRealtimeControlsTimer()
    return
  }
  showRealtimeControls()
}

watch(realtimeIntervalSeconds, () => {
  if (realtimeVisible.value) startRealtimeTimer()
})

watch(realtimePeriod, () => {
  if (realtimeVisible.value) startRealtimeTimer()
})

watch(
  () => dateRange.value,
  (value) => {
    if (suppressDateAutoQuery || !value?.[0] || !value?.[1]) return
    if (dateAutoQueryTimer) clearTimeout(dateAutoQueryTimer)
    dateAutoQueryTimer = setTimeout(() => {
      loadDashboard()
    }, 120)
  },
  { deep: true }
)

onMounted(async () => {
  const today = formatDate(new Date())
  dateRange.value = [today, today]
  await loadDashboard()
  suppressDateAutoQuery = false
  startDashboardRefreshTimer()
  window.addEventListener('resize', resizeCharts)
})

onBeforeUnmount(() => {
  stopRealtimeTimer()
  stopDashboardRefreshTimer()
  window.removeEventListener('resize', resizeCharts)
  if (salesTrendChart) salesTrendChart.dispose()
  if (conversionChart) conversionChart.dispose()
  if (orderMixChart) orderMixChart.dispose()
  if (dateAutoQueryTimer) clearTimeout(dateAutoQueryTimer)
})
</script>

<style scoped>
.official-dashboard {
  min-height: 100vh;
  padding: 18px 20px 32px;
  color: #132031;
  background: linear-gradient(160deg, #f5f7fb 0%, #eef6f2 54%, #fff8ef 100%);
}

.dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 18px;
  border: 1px solid #dbe7ef;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 8px 24px rgba(19, 32, 49, 0.06);
}

.dashboard-header__eyebrow {
  margin: 0 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0d9488;
}

.dashboard-header h1,
.realtime-toolbar h2 {
  margin: 0;
  font-size: 24px;
  color: #0f172a;
}

.dashboard-header__desc {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.dashboard-actions,
.realtime-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.meta-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin: 12px 0;
  color: #48617b;
  font-size: 13px;
}

.meta-bar span {
  padding: 5px 10px;
  border: 1px solid #dbe7ef;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.75);
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 14px;
}

.kpi-card {
  min-height: 112px;
  padding: 14px;
  border: 1px solid #dbe7ef;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 6px 16px rgba(19, 32, 49, 0.05);
}

.kpi-card__label,
.kpi-card__kpi {
  margin: 0;
  color: #64748b;
  font-size: 12px;
}

.kpi-card__value {
  margin: 10px 0 8px;
  font-size: 24px;
  font-weight: 800;
  color: #102033;
}

.chart-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(0, 1fr) minmax(0, 0.9fr);
  gap: 14px;
  margin-bottom: 14px;
}

.chart-box {
  width: 100%;
  height: 300px;
}

.panel-card {
  border: 1px solid #dbe7ef;
  border-radius: 8px;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-weight: 700;
}

.panel-tip {
  color: #64748b;
  font-size: 12px;
  font-weight: 400;
}

:deep(.metric-cell) {
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.2;
}

:deep(.metric-cell small) {
  font-size: 11px;
  font-weight: 700;
}

:deep(.metric-cell .is-up) {
  color: #dc2626;
}

:deep(.metric-cell .is-down) {
  color: #16a34a;
}

:deep(.metric-cell .is-flat) {
  color: #94a3b8;
}

.realtime-overlay {
  position: fixed;
  inset: 0;
  z-index: 2500;
  display: flex;
  flex-direction: column;
  padding: 32px;
  color: #f8fafc;
  background:
    radial-gradient(circle at 18% 18%, rgba(13, 148, 136, 0.28), transparent 30%),
    radial-gradient(circle at 82% 72%, rgba(245, 158, 11, 0.22), transparent 28%),
    #08111f;
}

.realtime-toolbar {
  position: absolute;
  top: 32px;
  left: 32px;
  right: 32px;
  z-index: 2505;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.realtime-toolbar.is-hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(-18px);
}

.realtime-toolbar h2 {
  color: #f8fafc;
}

.realtime-content {
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
  gap: 24px;
  align-items: center;
  min-width: 0;
}

.realtime-metric {
  padding: 36px;
  border: 1px solid rgba(45, 212, 191, 0.38);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.72);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.28);
}

.realtime-metric span {
  display: block;
  margin-bottom: 18px;
  color: #99f6e4;
  font-size: 22px;
  font-weight: 700;
}

.realtime-metric strong {
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: clamp(40px, 6.8vw, 108px);
  line-height: 1;
  color: #f8fafc;
}

.realtime-metric--pay {
  border-color: rgba(251, 191, 36, 0.42);
}

.realtime-metric--pay span {
  color: #fde68a;
}

.realtime-foot {
  display: flex;
  justify-content: center;
  gap: 18px;
  color: #cbd5e1;
  font-size: 14px;
}

.realtime-pull-switch {
  position: fixed;
  top: 0;
  right: 30px;
  z-index: 2510;
  width: 34px;
  height: 82px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.realtime-pull-switch::before {
  content: '';
  position: absolute;
  top: 0;
  left: 50%;
  width: 2px;
  height: 54px;
  background: linear-gradient(180deg, rgba(248, 250, 252, 0.9), rgba(148, 163, 184, 0.35));
  transform: translateX(-50%);
}

.realtime-pull-switch span {
  position: absolute;
  left: 50%;
  bottom: 0;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.42);
  background: rgba(15, 23, 42, 0.92);
  box-shadow: 0 0 18px rgba(45, 212, 191, 0.34);
  transform: translateX(-50%);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.realtime-pull-switch span::after {
  content: '';
  position: absolute;
  inset: 9px;
  border-radius: 999px;
  background: #2dd4bf;
}

.realtime-pull-switch:hover span,
.realtime-pull-switch.is-active span {
  background: rgba(13, 148, 136, 0.95);
  box-shadow: 0 0 26px rgba(45, 212, 191, 0.72);
  transform: translateX(-50%) translateY(3px);
}

@media (max-width: 960px) {
  .dashboard-header,
  .realtime-toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .dashboard-actions,
  .realtime-actions {
    justify-content: flex-start;
  }

  .chart-grid {
    grid-template-columns: 1fr;
  }

  .realtime-content {
    grid-template-columns: 1fr;
  }
}
</style>
